import { BaseService } from "../BaseService";

export interface TenantDomainDnsRecord {
  type: string;
  host: string;
  data: string;
  valid?: boolean | null;
}

export interface TenantDomain {
  id: number;
  hostname: string;
  provider: string;
  provider_domain_id: string | null;
  dns_records: TenantDomainDnsRecord[];
  email_verified_at: string | null;
  last_verify_check_at: string | null;
  avatar_file_id: number | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  is_verified: boolean;
}

export interface TenantSenderAddress {
  id: number;
  address: string;
  display_name: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface TenantDomainsResponse {
  domains: TenantDomain[];
  sender_addresses: TenantSenderAddress[];
}

export class TenantDomainService extends BaseService {
  public async list(): Promise<TenantDomainsResponse> {
    return this.getRequest<TenantDomainsResponse>("/tenant-domains");
  }

  public async register(hostname: string): Promise<TenantDomain> {
    return this.postRequest<{ hostname: string }, TenantDomain>(
      "/tenant-domains",
      { hostname },
    );
  }

  public async verify(id: number): Promise<TenantDomain> {
    return this.postRequest<unknown, TenantDomain>(
      `/tenant-domains/${id}/verify`,
      {},
    );
  }

  public async remove(id: number): Promise<void> {
    await this.deleteRequest(`/tenant-domains/${id}`);
  }

  public async createSender(
    address: string,
    displayName: string | null,
    makeDefault: boolean,
  ): Promise<TenantSenderAddress> {
    return this.postRequest<
      { address: string; display_name: string | null; make_default: boolean },
      TenantSenderAddress
    >("/tenant-sender-addresses", {
      address,
      display_name: displayName,
      make_default: makeDefault,
    });
  }

  public async updateSender(id: number, displayName: string | null): Promise<TenantSenderAddress> {
    return this.putRequest<{ display_name: string | null }, TenantSenderAddress>(
      `/tenant-sender-addresses/${id}`,
      { display_name: displayName },
    );
  }

  public async makeSenderDefault(id: number): Promise<void> {
    await this.putRequest<unknown, void>(`/tenant-sender-addresses/${id}/default`, {});
  }

  public async removeSender(id: number): Promise<void> {
    await this.deleteRequest(`/tenant-sender-addresses/${id}`);
  }

  public async setAvatar(domainId: number, fileId: number): Promise<TenantDomain> {
    return this.putRequest<{ file_id: number }, TenantDomain>(
      `/tenant-domains/${domainId}/avatar`,
      { file_id: fileId },
    );
  }

  public async clearAvatar(domainId: number): Promise<void> {
    await this.deleteRequest(`/tenant-domains/${domainId}/avatar`);
  }
}

export const tenantDomainService = new TenantDomainService();
